import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Grid,
  LinearProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import API from '../../../services/API'
import { formatDateTime } from '../../kit/helpers'
import AgreementCard from './AgreementCard'
import EmailDraftDialog from './EmailDraftDialog'
import { AddGrantDialog, RevokeGrantDialog } from './GrantDialogs'
import TransitionDialog from './TransitionDialog'
import {
  AdvisorySentDialog,
  CustomerInformedDialog,
  QuoteSentDialog,
  ResetDialog,
  SeasonalReviewDialog,
} from './UsageActionDialogs'
import {
  AllowancesCard,
  GrantsCard,
  HistoryCard,
  MonthlyUsageCard,
  QuoteCard,
  RequiredActionCard,
  UpgradeCard,
} from './UsageSections'

const LOAD_ERROR = 'Could not load commercial usage for this tenant.'

const errorMessage = (res, fallback) => {
  if (res.status === 401) return 'Your CRM session has expired - sign in again.'
  return res.data?.message || res.data?.error || fallback
}

// The Commercial usage tab of a tenant record: effective allowances, monthly
// usage, the manual exceedance review, agreement evidence, complimentary
// capacity and the Enterprise+ transition. It owns its fetch, busy and error
// state. One GET loads everything; every action POST answers with the
// refreshed payload, which replaces the panel's state. Nothing here emails a
// customer or touches Stripe - the actions record what staff did by hand.
// `onLoaded(panel)` hands each payload to the page (tab badge, header tile);
// `onChanged(panel)` also tells it an action was recorded, so it can re-read
// the tenant (contract, activity timeline).
const CommercialUsagePanel = ({ tenantID, tenant, hasStripe, onOpenStripe, onLoaded, onChanged }) => {
  const base = `/admincrm/tenants/${tenantID}/commercial-usage`
  const [panel, setPanel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  // `open` is separate from the details so a dialog keeps its content while
  // it closes. The draft dialog has its own state: it can sit on top of the
  // quote dialog.
  const [dialog, setDialog] = useState({ open: false, type: null })
  const [draft, setDraft] = useState({ open: false })
  const current = useRef(tenantID)
  current.current = tenantID

  const load = async ({ quiet = false } = {}) => {
    const requestedFor = tenantID
    if (!quiet) {
      setLoading(true)
      setError('')
    }
    try {
      const res = await API.get(base)
      if (current.current !== requestedFor) return
      if (res.status === 200 && res.data) {
        setPanel(res.data)
        onLoaded?.(res.data)
      } else if (!quiet) {
        setError(errorMessage(res, LOAD_ERROR))
      }
    } catch (e) {
      if (!quiet && current.current === requestedFor) setError(LOAD_ERROR)
    } finally {
      if (!quiet && current.current === requestedFor) setLoading(false)
    }
  }

  useEffect(() => {
    setPanel(null)
    load()
  }, [tenantID])

  // Posts one action. 200 carries the refreshed panel; anything else carries
  // a message for the dialog that asked.
  const act = async (path, body, done) => {
    try {
      const res = await API.post(`${base}/${path}`, body)
      if (res.status === 200 && res.data) {
        setPanel(res.data)
        onChanged?.(res.data)
        setToast(res.data.alreadyRecorded ? 'That had already been recorded - nothing was changed.' : done)
        return { ok: true }
      }
      // 409: the record moved on underneath this request. Bring the panel
      // behind the dialog up to date so the message makes sense.
      if (res.status === 409) load({ quiet: true })
      return { ok: false, error: errorMessage(res, 'That did not save.') }
    } catch (e) {
      return { ok: false, error: 'The request failed - check your connection and try again.' }
    }
  }

  const openDialog = (type, details = {}) => {
    if (type === 'draft') {
      setDraft({ open: true, period: details.period, stage: details.stage, query: details.query })
      return
    }
    setDialog({ open: true, type, ...details })
  }
  const closeDialog = () => setDialog((prev) => ({ ...prev, open: false }))
  const isOpen = (type) => dialog.open && dialog.type === type

  if (!panel) {
    if (loading) return <LinearProgress sx={{ borderRadius: 1 }} />
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => load()}>
            Try again
          </Button>
        }
      >
        {error || LOAD_ERROR}
      </Alert>
    )
  }

  const records = panel.records ?? []
  const target = dialog.target
  const period = dialog.period
  const revisedQuote = (target?.action?.status ?? target?.status) === 'upgradeRequired'
  // A revised quote starts from the quote already on record: this month's
  // own, else the latest recorded on an earlier month that has not been reset.
  const previousQuote = !revisedQuote
    ? null
    : target?.action?.quoteReference
      ? target.action
      : (records.find(
          (record) =>
            record.period < period && record.action?.quoteReference && record.action?.status !== 'voided',
        )?.action ?? null)
  const resettableMonths = records
    .filter((record) => (record.allowedActions ?? []).includes('reset'))
    .map((record) => record.period)

  return (
    <Stack gap={2}>
      {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {panel.liveFiguresAvailable === false && (
        <Alert severity="info">
          Live figures are unavailable because this tenant&apos;s database could not be reached.
          Months still open show the figures stored by the last nightly run, and recorded months
          could not be re-checked against current data.
        </Alert>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          As at {formatDateTime(panel.generatedAt)} · AmbuNet staff record what they did here;
          nothing is emailed to the customer and Stripe is never changed from this page.
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack gap={2}>
            <RequiredActionCard panel={panel} onOpen={openDialog} />
            <AllowancesCard panel={panel} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}>
          <AgreementCard agreement={panel.agreement} />
        </Grid>
        <Grid item xs={12}>
          <MonthlyUsageCard panel={panel} onOpen={openDialog} />
        </Grid>
        <Grid item xs={12} lg={7}>
          <Stack gap={2}>
            <QuoteCard panel={panel} />
            <HistoryCard panel={panel} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Stack gap={2}>
            <GrantsCard panel={panel} onOpen={openDialog} />
            <UpgradeCard panel={panel} onOpen={openDialog} />
          </Stack>
        </Grid>
      </Grid>

      <AdvisorySentDialog
        open={isOpen('advisory')}
        target={target}
        onClose={closeDialog}
        onSubmit={(body) => act(`${period}/advisory-sent`, body, 'Advisory recorded as sent.')}
      />
      <QuoteSentDialog
        open={isOpen('quote')}
        target={target}
        currency={tenant?.contract?.currency}
        previousQuote={previousQuote}
        onClose={closeDialog}
        onSubmit={(body) =>
          act(`${period}/quote-sent`, body, revisedQuote ? 'Revised quote recorded.' : 'Quote recorded as sent.')
        }
        onPreviewDraft={
          (target?.emailDraftStages ?? []).includes('quote')
            ? (query) => openDialog('draft', { period, stage: 'quote', query })
            : undefined
        }
      />
      <SeasonalReviewDialog
        open={isOpen('seasonal')}
        target={target}
        onClose={closeDialog}
        onSubmit={(body) => act(`${period}/seasonal-review`, body, 'Seasonal review - no change recorded.')}
      />
      <CustomerInformedDialog
        open={isOpen('informed')}
        target={target}
        onClose={closeDialog}
        onSubmit={(body) =>
          act(`${period}/seasonal-review/customer-informed`, body, 'Customer recorded as informed.')
        }
      />
      <ResetDialog
        open={isOpen('reset')}
        initialPeriod={period}
        months={resettableMonths}
        sequencePeriods={panel.sequence?.exceededPeriods ?? []}
        confirmText={panel.tenant?.name ?? tenant?.name ?? 'RESET'}
        onClose={closeDialog}
        onSubmit={(body) => act('reset', body, 'Reset after correction recorded.')}
      />
      <AddGrantDialog
        open={isOpen('grant')}
        records={records}
        onClose={closeDialog}
        onSubmit={(body) => act('grants', body, 'Complimentary capacity added.')}
      />
      <RevokeGrantDialog
        open={isOpen('revoke')}
        grant={dialog.grant}
        onClose={closeDialog}
        onSubmit={(body) => act(`grants/${dialog.grant?._id}/revoke`, body, 'Complimentary capacity revoked.')}
      />
      <TransitionDialog
        open={isOpen('transition')}
        tenant={tenant}
        panel={panel}
        hasStripe={hasStripe}
        onOpenStripe={onOpenStripe}
        onClose={closeDialog}
        onSubmit={(body) =>
          act('transition', body, 'Enterprise+ transition recorded - nothing was changed in Stripe.')
        }
      />
      <EmailDraftDialog
        open={draft.open}
        tenantID={tenantID}
        period={draft.period}
        stage={draft.stage}
        query={draft.query}
        onClose={() => setDraft((prev) => ({ ...prev, open: false }))}
        onToast={setToast}
      />

      {/* Click-away is ignored: the click that raises a toast would otherwise
          dismiss it when an earlier toast is still showing. */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={(event, reason) => {
          if (reason !== 'clickaway') setToast('')
        }}
        message={toast}
      />
    </Stack>
  )
}

export default CommercialUsagePanel
